import { styled } from '@linaria/react';
import { useLayoutEffect, useRef, useState } from 'react';
import type { AgentMinerSession } from '../../../../api/agent-miner-client/agent-miner-client.type';
import { inkDarkest } from '../../../../styles/design-tokens';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { getSessionDisplayTitle } from '../../shared/shared.helpers';

type TitleCellProps = {
  session: AgentMinerSession;
};

/**
 * Renders the session display title, truncating with an ellipsis when it
 * overflows the column. Shows the full title in a tooltip on hover, but
 * only when the text is actually clipped.
 *
 * @param {TitleCellProps} props - Session row for the cell
 * @returns {JSX.Element} The title cell, optionally wrapped in a Tooltip
 */
export function TitleCell({ session }: TitleCellProps) {
  const displayName = getSessionDisplayTitle(session);
  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflowed, setIsOverflowed] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: displayName drives the DOM the effect measures, so we must re-run on title changes
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;
    setIsOverflowed(el.scrollWidth > el.clientWidth);
  }, [displayName]);

  const cell = (
    <Wrapper>
      <NameText ref={textRef}>{displayName}</NameText>
    </Wrapper>
  );

  if (!isOverflowed) return cell;

  return (
    <Tooltip label={displayName} variant="black" multiline style={{ maxWidth: 260 }}>
      {cell}
    </Tooltip>
  );
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 250px;
`;

const NameText = styled.span`
  font-family: 'Figtree', sans-serif;
  font-weight: 500;
  font-size: 12.5px;
  line-height: 1.2;
  color: ${inkDarkest};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;
